/**
 * @fileoverview The plan badge beside an instructor's name on Settings.
 *
 * Four tiers, and the label always comes from the shared catalogue — this used
 * to be a two-way ternary that called `concertmaster` "Studio" (which is a
 * different, cheaper, AI-free plan) and everything else "Pro", so a free
 * account and a $19 Studio account both wore a badge for a plan neither was on.
 *
 * The fallthrough NAMES an unrecognised tier rather than calling it Solo: a
 * key this build does not know means a backend running ahead of it, and
 * telling a paying instructor they are on the free plan is the one wrong
 * answer that costs something.
 */

import { isPaidTier, tierDisplayName } from "@/lib/tiers";

const BASE = "inline-flex items-center rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest";

export default function TierBadge({ tier, testId = "tier-badge" }) {
  if (tier === "concertmaster") {
    // The top plan wears the same gold as the founding medal: lit metal, not
    // a flat yellow chip. The metal is deliberately the SAME in both themes:
    // a dark gold-on-onyx chip is legible on a white card as it is on a dark
    // one, and it is the founding-medal look — so these stay literals rather
    // than tokens (decorative, and the one badge that must not follow the
    // theme).
    return (
      <span
        data-testid={testId}
        className={BASE}
        style={{
          background: "linear-gradient(160deg, #111008 0%, #1e1a04 50%, #0e0c04 100%)", // brand-lint-ignore
          color: "#d4a820", // brand-lint-ignore
          border: "1px solid #c89418", // brand-lint-ignore
          boxShadow: "0 0 10px rgba(200, 148, 20, 0.3), 0 0 2px rgba(200, 148, 20, 0.4)", // brand-lint-ignore
          textShadow: "0 0 8px rgba(212, 168, 32, 0.5)", // brand-lint-ignore
        }}
      >
        {tierDisplayName(tier)}
      </span>
    );
  }
  if (tier === "pro") {
    // Legacy tier — nobody can buy it, but grandfathered subscribers still see
    // it, so it keeps a colour of its own rather than being folded into the
    // paid-tier chip. It wore a violet that had no token and was unreadable
    // on a white card, so it now takes the cool accent — the studio's one
    // second hue, which the light theme darkens for legibility.
    return (
      <span
        data-testid={testId}
        className={BASE}
        style={{ background: "var(--accent-cool-dim)", color: "var(--accent-cool)", border: "1px solid var(--accent-cool-dim)" }}
      >
        {tierDisplayName(tier)}
      </span>
    );
  }
  if (isPaidTier(tier)) {
    return (
      <span
        data-testid={testId}
        className={`${BASE} border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]`}
      >
        {tierDisplayName(tier)}
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className={`${BASE} border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] text-[var(--text-secondary)]`}
    >
      {tierDisplayName(tier)}
    </span>
  );
}
